import React, { useState } from 'react';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';
import './Avatar.css';

/**
 * Avatar Component
 * User avatar with fallback, sizes, and status indicator
 *
 * @param {Object} props - Component props
 * @param {string} props.src - Image source URL
 * @param {string} props.alt - Alt text for image
 * @param {string} props.fallback - Fallback text (initials)
 * @param {string} props.size - Size variant: 'xs', 'sm', 'md', 'lg', 'xl'
 * @param {string} props.status - Status indicator: 'online', 'offline', 'away', 'busy'
 * @param {boolean} props.showStatus - Show status indicator
 * @param {string} props.shape - Shape variant: 'circle', 'square'
 * @param {string} props.className - Additional CSS classes
 */
const Avatar = ({
  src,
  alt = 'Avatar',
  fallback,
  size = 'md',
  status,
  showStatus = false,
  shape = 'circle',
  className = '',
}) => {
  const [imageError, setImageError] = useState(false);

  const handleImageError = () => {
    setImageError(true);
  };

  const getFallbackText = () => {
    if (fallback) return fallback;
    if (alt) {
      // Generate initials from alt text
      const words = alt.split(' ');
      if (words.length >= 2) {
        return `${words[0][0]}${words[1][0]}`.toUpperCase();
      }
      return alt[0].toUpperCase();
    }
    return '?';
  };

  const containerClasses = `
    avatar
    avatar--${size}
    avatar--${shape}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  const statusClasses = `
    avatar-status
    avatar-status--${status}
    avatar-status--${size}
  `.trim().replace(/\s+/g, ' ');

  return (
    <motion.div
      className={containerClasses}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2 }}
    >
      {src && !imageError ? (
        <img
          src={src}
          alt={alt}
          className="avatar-image"
          onError={handleImageError}
        />
      ) : (
        <div className="avatar-fallback">{getFallbackText()}</div>
      )}

      {showStatus && status && <span className={statusClasses} />}
    </motion.div>
  );
};

Avatar.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  fallback: PropTypes.string,
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
  status: PropTypes.oneOf(['online', 'offline', 'away', 'busy']),
  showStatus: PropTypes.bool,
  shape: PropTypes.oneOf(['circle', 'square']),
  className: PropTypes.string,
};

export default Avatar;
